import { useState, useEffect } from "react";
import { Order, OrderedProduct } from "src/shared/services/interfacev2";

interface UseOrderModificationTrackerProps {
  selectedProducts: OrderedProduct[];
  isEditing: boolean;
  oldProducts: Order | undefined;
  discount: number;
  tableNumber: string;
  guess: string;
  orderType: string;
  deliveryFee: number;
}

const getOldProductId = (product: any): string => (typeof product === "string" ? product : product?._id?.$oid || product?.productId || "");

const normalizeNote = (note?: string) => (note || "").trim();

export const useOrderModificationTracker = ({ selectedProducts, isEditing, oldProducts, discount, tableNumber, guess, orderType, deliveryFee }: UseOrderModificationTrackerProps) => {
  const [isOrderModified, setIsOrderModified] = useState(true);

  const checkForOrderChanges = (
    currentProducts: OrderedProduct[],
    oldProductsArray: any[],
    currentDiscount: number,
    oldDiscount: number,
    currentTableNumber: string,
    oldTableNumber: string,
    currentGuess: string,
    oldGuess: string,
    currentOrderType: string,
    oldOrderType: string,
    currentDeliveryFee: number,
    oldDeliveryFee: number
  ): boolean => {
    if (currentTableNumber !== oldTableNumber) return true;
    if (currentGuess !== oldGuess) return true;
    if (currentDiscount !== oldDiscount) return true;
    if (currentOrderType !== oldOrderType) return true;
    if (currentDeliveryFee !== oldDeliveryFee) return true;
    if (currentProducts.length === 0 && (!oldProductsArray || oldProductsArray.length === 0)) {
      return false;
    }

    if (currentProducts.length !== oldProductsArray.length) {
      return true;
    }

    /* -------- MAP PRODUCTS -------- */

    const currentMap = new Map<string, { quantity: number; note: string }>();

    const oldMap = new Map<string, { quantity: number; note: string }>();

    // Current (UI)
    currentProducts.forEach((product) => {
      currentMap.set(product.productId, {
        quantity: product.quantity,
        note: normalizeNote(JSON.stringify(product.notes ?? [])),
      });
    });

    // Old (Order from backend)
    oldProductsArray.forEach((product) => {
      const productId = getOldProductId(product);
      if (!productId) return;

      oldMap.set(productId, {
        quantity: Number(product.quantity || 0),
        note: normalizeNote(product.note || product.notes),
      });
    });

    /* -------- KEY CHECK -------- */

    const currentKeys = Array.from(currentMap.keys()).sort();
    const oldKeys = Array.from(oldMap.keys()).sort();

    if (JSON.stringify(currentKeys) !== JSON.stringify(oldKeys)) {
      return true;
    }

    /* -------- VALUE CHECK -------- */

    for (const id of currentKeys) {
      const current = currentMap.get(id)!;
      const old = oldMap.get(id);

      if (!old) return true;
      if (current.quantity !== old.quantity) return true;
      if (current.note !== old.note) return true;
    }

    return false;
  };

  useEffect(() => {
    if (isEditing && oldProducts) {
      const order = oldProducts.orders[0];

      const oldDiscount = Number(order.discount || 0);
      const oldTableNumber = String(order.tableNumber ?? "");
      const oldGuess = String(order.guest ?? "");

      const hasChanges = checkForOrderChanges(selectedProducts, oldProducts.orderDetails[0].products, discount, oldDiscount, String(tableNumber ?? ""), oldTableNumber, String(guess ?? ""), oldGuess,  orderType, oldProducts.orderDetails[0].orderType ?? "", deliveryFee, Number(order.deliveryFee) || 0);

      setIsOrderModified(hasChanges);
    } else {
      // New order → modified if products exist
      setIsOrderModified(selectedProducts.length > 0);
    }
  }, [selectedProducts, isEditing, oldProducts, discount, tableNumber, guess, orderType, deliveryFee]);

  return { isOrderModified, setIsOrderModified };
};
